import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { WorkingHours, Holidays, SlaMasters } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Clock, CalendarDays, Plus, Trash2, Save, Gauge } from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = [
  { v: 0, label: "Mon" }, { v: 1, label: "Tue" }, { v: 2, label: "Wed" },
  { v: 3, label: "Thu" }, { v: 4, label: "Fri" }, { v: 5, label: "Sat" },
  { v: 6, label: "Sun" },
];

export default function SlaMastersPanel() {
  return (
    <Card className="border-slate-200" data-testid="sla-masters-panel">
      <CardHeader>
        <CardTitle className="font-display text-lg">SLA Engine · Working Hours & Holidays</CardTitle>
        <p className="text-sm text-slate-500">
          Per-location operating hours and the holiday calendar power the business-hour SLA timers on every task.
        </p>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="hours">
          <TabsList data-testid="sla-tabs">
            <TabsTrigger value="hours" data-testid="sla-tab-hours">
              <Clock className="mr-1.5 h-3.5 w-3.5" /> Working Hours
            </TabsTrigger>
            <TabsTrigger value="holidays" data-testid="sla-tab-holidays">
              <CalendarDays className="mr-1.5 h-3.5 w-3.5" /> Holidays
            </TabsTrigger>
            <TabsTrigger value="masters" data-testid="sla-tab-masters">
              <Gauge className="mr-1.5 h-3.5 w-3.5" /> SLA Master
            </TabsTrigger>
          </TabsList>
          <TabsContent value="hours" className="mt-4">
            <WorkingHoursList />
          </TabsContent>
          <TabsContent value="holidays" className="mt-4">
            <HolidaysList />
          </TabsContent>
          <TabsContent value="masters" className="mt-4">
            <SlaMasterTable />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function WorkingHoursList() {
  const { locations } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    const all = await WorkingHours.list();
    const byLoc = Object.fromEntries(all.map((r) => [r.location_id, r]));
    const merged = locations.map((l) => byLoc[l.id] || {
      location_id: l.id, start_time: "10:00", end_time: "19:00", weekly_offs: [6],
    });
    setRows(merged);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, [locations]);

  if (loading) return <div className="py-8 text-center text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="working-hours-list">
      {rows.map((row) => (
        <WorkingHoursRow
          key={row.location_id}
          row={row}
          location={locations.find((l) => l.id === row.location_id)}
          onSaved={refresh}
        />
      ))}
    </div>
  );
}

function WorkingHoursRow({ row, location, onSaved }) {
  const [start, setStart] = useState(row.start_time || "10:00");
  const [end, setEnd] = useState(row.end_time || "19:00");
  const [offs, setOffs] = useState(row.weekly_offs || [6]);
  const [saving, setSaving] = useState(false);

  const toggle = (d) => setOffs((curr) => curr.includes(d)
    ? curr.filter((x) => x !== d) : [...curr, d].sort());

  const save = async () => {
    setSaving(true);
    try {
      await WorkingHours.upsert({
        location_id: row.location_id, start_time: start, end_time: end, weekly_offs: offs,
      });
      toast.success(`${location?.code || ""} hours saved`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4"
      data-testid={`wh-row-${location?.code || row.location_id}`}
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[140px]">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Location</div>
          <div className="mt-1 font-display text-base font-semibold text-slate-900">
            {location?.code} <span className="ml-1 text-sm font-medium text-slate-500">{location?.name}</span>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Start</Label>
          <Input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                 className="w-[120px]" data-testid={`wh-start-${location?.code}`} />
        </div>
        <div>
          <Label className="text-xs text-slate-600">End</Label>
          <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)}
                 className="w-[120px]" data-testid={`wh-end-${location?.code}`} />
        </div>
        <div className="flex-1 min-w-[280px]">
          <Label className="text-xs text-slate-600">Weekly offs</Label>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {WEEKDAYS.map((d) => {
              const active = offs.includes(d.v);
              return (
                <button
                  key={d.v}
                  type="button"
                  onClick={() => toggle(d.v)}
                  data-testid={`wh-off-${location?.code}-${d.label}`}
                  className={`rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
                    active
                      ? "border-rose-300 bg-rose-50 text-rose-700"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid={`wh-save-${location?.code}`}>
          <Save className="mr-1.5 h-3.5 w-3.5" /> Save
        </Button>
      </div>
    </div>
  );
}

function HolidaysList() {
  const { locations } = useApp();
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [scope, setScope] = useState("national");
  const [locId, setLocId] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = async () => setRows(await Holidays.list());
  useEffect(() => { refresh(); }, []);

  const add = async () => {
    if (!name.trim() || !date) {
      toast.error("Name and date are required");
      return;
    }
    if (scope === "local" && !locId) {
      toast.error("Pick a location for local holiday");
      return;
    }
    setSaving(true);
    try {
      await Holidays.create({
        name: name.trim(), date, scope,
        location_id: scope === "local" ? locId : null,
      });
      setName(""); setDate(""); setScope("national"); setLocId("");
      toast.success("Holiday added");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add holiday");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this holiday?")) return;
    await Holidays.remove(id);
    refresh();
  };

  const locName = (id) => locations.find((l) => l.id === id)?.code || id;

  return (
    <div className="space-y-4" data-testid="holidays-list">
      <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs text-slate-600">Holiday name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g., Republic Day"
                   data-testid="holiday-name" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   data-testid="holiday-date" />
          </div>
          <div className="min-w-[140px]">
            <Label className="text-xs text-slate-600">Scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="holiday-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="national">National</SelectItem>
                <SelectItem value="local">Local</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {scope === "local" && (
            <div className="min-w-[160px]">
              <Label className="text-xs text-slate-600">Location</Label>
              <Select value={locId} onValueChange={setLocId}>
                <SelectTrigger data-testid="holiday-location">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.code} — {l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={add} disabled={saving} data-testid="holiday-add">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Holiday
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 px-6 py-8 text-center text-sm text-slate-500">
          No holidays configured yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Scope</th>
                <th className="px-4 py-2.5">Location</th>
                <th className="px-4 py-2.5 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-white">
              {rows.map((h) => (
                <tr key={h.id} data-testid={`holiday-row-${h.id}`}>
                  <td className="px-4 py-2 font-mono-data text-xs">{h.date}</td>
                  <td className="px-4 py-2 text-slate-900">{h.name}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                      h.scope === "national"
                        ? "bg-primary/10 text-primary"
                        : "bg-amber-50 text-amber-800"
                    }`}>
                      {h.scope}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {h.scope === "local" ? locName(h.location_id) : "All"}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Button variant="ghost" size="icon" onClick={() => remove(h.id)}
                            data-testid={`holiday-delete-${h.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ---------- SLA Master ----------------------------------------------------

const DEFAULT_NEW_ROW = {
  workflow_stage: "PDI_VIN",
  from_status: "",
  to_status: "",
  sla_value: 60,
  unit: "minutes",
  working_hours_applicable: true,
  active: true,
  location_id: "",
};

function SlaMasterTable() {
  const { locations } = useApp();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ workflow_stages: [], units: [], stage_labels: {} });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(DEFAULT_NEW_ROW);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const rowsP = SlaMasters.list();
      // meta is static — only fetch it on the first load.
      const metaP = meta.workflow_stages.length
        ? Promise.resolve(meta)
        : SlaMasters.meta();
      const [r, m] = await Promise.all([rowsP, metaP]);
      setRows(r);
      if (!meta.workflow_stages.length) setMeta(m);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load SLA masters");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const locName = (id) => {
    if (!id) return "Global (all locations)";
    const l = locations.find((x) => x.id === id);
    return l ? `${l.code} — ${l.name}` : id;
  };

  const stageLabel = (s) => meta.stage_labels[s] || s;

  const add = async () => {
    if (!form.workflow_stage) { toast.error("Workflow stage required"); return; }
    if (form.unit !== "dynamic" && (!form.sla_value || Number(form.sla_value) <= 0)) {
      toast.error("SLA value must be greater than zero");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        workflow_stage: form.workflow_stage,
        from_status: form.from_status?.trim() || null,
        to_status: form.to_status?.trim() || null,
        sla_value: form.unit === "dynamic" ? 0 : Number(form.sla_value),
        unit: form.unit,
        working_hours_applicable: !!form.working_hours_applicable,
        active: !!form.active,
        location_id: form.location_id || null,
      };
      await SlaMasters.create(payload);
      setForm(DEFAULT_NEW_ROW);
      toast.success("SLA Master added");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add SLA");
    } finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    try {
      await SlaMasters.update(row.id, { active: !row.active });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const updateValue = async (row, patch) => {
    try {
      await SlaMasters.update(row.id, patch);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete SLA for ${stageLabel(row.workflow_stage)}?`)) return;
    try {
      await SlaMasters.remove(row.id);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="py-8 text-center text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="sla-masters-table">
      {/* Add row */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Add SLA Row
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <Label className="text-xs text-slate-600">Workflow Stage</Label>
            <Select value={form.workflow_stage}
                    onValueChange={(v) => setForm({ ...form, workflow_stage: v })}>
              <SelectTrigger data-testid="sla-add-stage"><SelectValue /></SelectTrigger>
              <SelectContent>
                {meta.workflow_stages.map((s) => (
                  <SelectItem key={s} value={s}>{stageLabel(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[140px]">
            <Label className="text-xs text-slate-600">From Status</Label>
            <Input value={form.from_status} placeholder="e.g., Sales Released"
                   onChange={(e) => setForm({ ...form, from_status: e.target.value })}
                   data-testid="sla-add-from" />
          </div>
          <div className="min-w-[140px]">
            <Label className="text-xs text-slate-600">To Status</Label>
            <Input value={form.to_status} placeholder="e.g., VIN Assignment"
                   onChange={(e) => setForm({ ...form, to_status: e.target.value })}
                   data-testid="sla-add-to" />
          </div>
          <div className="w-[100px]">
            <Label className="text-xs text-slate-600">SLA Value</Label>
            <Input type="number" min="0" step="0.5"
                   value={form.sla_value}
                   disabled={form.unit === "dynamic"}
                   onChange={(e) => setForm({ ...form, sla_value: e.target.value })}
                   data-testid="sla-add-value" />
          </div>
          <div className="w-[120px]">
            <Label className="text-xs text-slate-600">Unit</Label>
            <Select value={form.unit}
                    onValueChange={(v) => setForm({ ...form, unit: v })}>
              <SelectTrigger data-testid="sla-add-unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                {meta.units.map((u) => (
                  <SelectItem key={u} value={u}>{u.charAt(0).toUpperCase()+u.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <Label className="text-xs text-slate-600">Location (optional)</Label>
            <Select value={form.location_id || "__global__"}
                    onValueChange={(v) => setForm({ ...form, location_id: v === "__global__" ? "" : v })}>
              <SelectTrigger data-testid="sla-add-location"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__global__">Global (all locations)</SelectItem>
                {locations.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.code} — {l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <Switch checked={!!form.working_hours_applicable}
                    onCheckedChange={(v) => setForm({ ...form, working_hours_applicable: v })}
                    data-testid="sla-add-wh" />
            <Label className="text-xs text-slate-600 cursor-pointer"
                   onClick={() => setForm({ ...form, working_hours_applicable: !form.working_hours_applicable })}>
              Working Hours
            </Label>
          </div>
          <Button onClick={add} disabled={saving} data-testid="sla-add-btn">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add SLA
          </Button>
        </div>
      </div>

      {/* Existing rows */}
      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 px-6 py-8 text-center text-sm text-slate-500">
          No SLA rules configured. Use the form above to add one.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2.5">Workflow Stage</th>
                <th className="px-3 py-2.5">From → To</th>
                <th className="px-3 py-2.5">SLA</th>
                <th className="px-3 py-2.5">Unit</th>
                <th className="px-3 py-2.5">Location</th>
                <th className="px-3 py-2.5 text-center">Working&nbsp;Hours</th>
                <th className="px-3 py-2.5 text-center">Active</th>
                <th className="px-2 py-2.5 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-white">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`sla-row-${r.workflow_stage}-${r.location_id || "global"}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">
                    {stageLabel(r.workflow_stage)}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {(r.from_status || r.to_status)
                      ? `${r.from_status || "—"} → ${r.to_status || "—"}`
                      : <span className="italic text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" min="0" step="0.5"
                           defaultValue={r.sla_value}
                           disabled={r.unit === "dynamic"}
                           className="h-8 w-20"
                           data-testid={`sla-value-${r.id}`}
                           onBlur={(e) => {
                             const v = Number(e.target.value);
                             if (v > 0 && v !== r.sla_value) updateValue(r, { sla_value: v });
                           }} />
                  </td>
                  <td className="px-3 py-2">
                    <Select value={r.unit}
                            onValueChange={(v) => updateValue(r, { unit: v })}>
                      <SelectTrigger className="h-8 w-[100px]"
                                     data-testid={`sla-unit-${r.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {meta.units.map((u) => (
                          <SelectItem key={u} value={u}>{u}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {locName(r.location_id)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Switch checked={!!r.working_hours_applicable}
                            onCheckedChange={(v) =>
                              updateValue(r, { working_hours_applicable: v })}
                            data-testid={`sla-wh-${r.id}`} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Switch checked={!!r.active}
                            onCheckedChange={() => toggleActive(r)}
                            data-testid={`sla-active-${r.id}`} />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Button variant="ghost" size="icon" onClick={() => remove(r)}
                            data-testid={`sla-delete-${r.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-md bg-blue-50/60 border border-blue-200 px-3 py-2 text-xs text-blue-900">
        <strong>How it works:</strong> Workflow stages read their SLA from this
        table. Location-specific rows override the Global default. Changes apply
        within ~60s (in-process cache) and are picked up by every new task and
        every SLA dashboard refresh. Working Hours, weekly offs and Holiday Master
        are honoured when <em>Working Hours</em> is ON.
      </div>
    </div>
  );
}